import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Shield, Feather } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const howWePray = [
    { icon: Shield, label: "With confidentiality" },
    { icon: Heart, label: "With compassion" },
    { icon: Feather, label: "With faith" },
    { icon: Heart, label: "With intention" },
    { icon: Shield, label: "With community" },
];

export default function WMWEPrayer() {
    const [form, setForm] = useState({ name: "", email: "", request: "", keep_private: false });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post(`${API}/prayer`, {
                name: form.name || null,
                email: form.email || null,
                request: form.request,
                keep_private: form.keep_private,
            });
            toast.success("We've received your request. You are covered.");
            setForm({ name: "", email: "", request: "", keep_private: false });
        } catch (err) {
            const msg = err?.response?.data?.detail || "Submission failed. Please try again.";
            toast.error(typeof msg === "string" ? msg : "Submission failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const inputClass =
        "w-full font-sans text-base bg-sanct-sand border border-sanct-clay/60 rounded-xl px-5 py-3.5 outline-none focus:border-sanct-rose transition-colors text-sanct-cocoa placeholder:text-sanct-cocoa/40";

    return (
        <main data-testid="wmwe-prayer" className="bg-sanct-sand text-sanct-charcoal">
            {/* Prayer Hero */}
            <section className="py-32 md:py-40 bg-sanct-cream text-center relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none" style={{
                    background: "radial-gradient(ellipse at center, transparent 0%, rgba(215,192,179,0.3) 100%)"
                }} />
                <div className="relative max-w-[760px] mx-auto px-6 md:px-10">
                    <div className="w-14 h-14 rounded-full bg-sanct-rose/20 flex items-center justify-center mx-auto mb-8">
                        <Heart size={24} className="text-sanct-rose" strokeWidth={1.5} />
                    </div>
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-6">The Prayer Room</p>
                    <h1 className="font-serif text-5xl md:text-7xl text-sanct-cocoa leading-[1] tracking-tight">
                        A safe place
                        <span className="block italic text-sanct-rose mt-2">to be held.</span>
                    </h1>
                    <p className="font-serif italic text-xl md:text-2xl text-sanct-cocoa/85 mt-12 leading-[1.6]">
                        You don&apos;t have to carry it alone. Share your prayer request, and our fellowship will cover you.
                    </p>
                </div>
            </section>

            {/* Form + How We Pray */}
            <section className="py-24 md:py-32 bg-sanct-sand">
                <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Form */}
                    <form onSubmit={submit} className="lg:col-span-7 bg-sanct-cream rounded-[28px] p-8 md:p-12 space-y-6 border border-sanct-clay/40 shadow-sm" data-testid="prayer-form">
                        <div>
                            <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-3">Submit a Prayer Request</p>
                            <h2 className="font-serif text-3xl md:text-4xl text-sanct-cocoa leading-tight">Share what&apos;s on your heart.</h2>
                        </div>
                        <div>
                            <label className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/70 block mb-2">Your Name (optional)</label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="You may remain anonymous"
                                className={inputClass}
                                data-testid="prayer-name-input"
                            />
                        </div>
                        <div>
                            <label className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/70 block mb-2">Email (optional)</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                placeholder="If you'd like a personal follow-up"
                                className={inputClass}
                                data-testid="prayer-email-input"
                            />
                        </div>
                        <div>
                            <label className="font-sans text-xs uppercase tracking-[0.25em] text-sanct-cocoa/70 block mb-2">Your Prayer Request *</label>
                            <textarea
                                required
                                rows={6}
                                value={form.request}
                                onChange={(e) => setForm({ ...form, request: e.target.value })}
                                placeholder="Share what's on your heart..."
                                className={`${inputClass} resize-none`}
                                data-testid="prayer-request-input"
                            />
                        </div>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.keep_private}
                                onChange={(e) => setForm({ ...form, keep_private: e.target.checked })}
                                className="w-4 h-4 accent-sanct-rose"
                                data-testid="prayer-private-checkbox"
                            />
                            <span className="font-sans text-sm text-sanct-charcoal/75">Keep this request private (Shana only — not shared with the fellowship)</span>
                        </label>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="prayer-submit-btn"
                            className="inline-flex items-center gap-3 px-9 py-5 bg-sanct-rose text-sanct-cream text-xs uppercase tracking-[0.25em] rounded-full hover:bg-sanct-cocoa transition-colors duration-300 disabled:opacity-50"
                        >
                            {loading ? "Sending..." : "Submit Your Prayer Request"}
                        </button>
                    </form>

                    {/* How We Pray */}
                    <aside className="lg:col-span-5">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-sanct-rose mb-5">How We Pray for You</p>
                        <h3 className="font-serif text-3xl md:text-4xl text-sanct-cocoa leading-tight mb-8">
                            With reverence. With care.
                        </h3>
                        <div className="space-y-3">
                            {howWePray.map((h) => {
                                const Icon = h.icon;
                                return (
                                    <div key={h.label} className="flex items-center gap-4 bg-sanct-cream rounded-[20px] p-5 border border-sanct-clay/40">
                                        <div className="w-10 h-10 rounded-full bg-sanct-rose/20 flex items-center justify-center flex-shrink-0">
                                            <Icon size={18} className="text-sanct-rose" strokeWidth={1.5} />
                                        </div>
                                        <p className="font-serif italic text-lg md:text-xl text-sanct-cocoa">{h.label}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </aside>
                </div>
            </section>

            {/* Closing */}
            <section className="py-28 md:py-36 bg-sanct-clay/30 text-center">
                <h2 className="font-serif italic text-5xl md:text-7xl text-sanct-cocoa leading-[1] tracking-tight px-6">
                    You are <span className="text-sanct-rose">not alone.</span>
                </h2>
            </section>
        </main>
    );
}
